import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
    ChevronRight,
    TrendingUp,
    Shield,
    Globe,
    Users,
    Award,
    Star,
    CheckCircle,
    Briefcase,
    BarChart4,
    Handshake,
    FileCheck2,
} from "lucide-react";
import { URLs } from "../lib/constants";

/**
 * Full SEO-optimized landing page for:
 * "Best Sports CA Firm in India"
 * - Alternating brand colors:
 *   Primary  : #00597b
 *   Secondary: #ccf1ff
 * - No header/footer; pure sections
 * - 1200+ words with keyword repetition (4–5 times)
 * - OG + Twitter meta tags + JSON-LD schema
 * - TailwindCSS classes
 */
const SportsCAWebsite = () => {
    const [expandedFAQ, setExpandedFAQ] = useState(null);

    // --- SERVICES ---
    const services = [
        {
            icon: <TrendingUp />,
            title: "Sports Tax Planning & Compliance",
            description:
                "Specialized tax planning for athletes with multi-stream income—league contracts, match fees, endorsements, prize money, appearance income, and bonuses—structured to minimize liabilities and ensure spotless compliance.",
            points: [
                "Year-round proactive tax planning (not just at filing time)",
                "Segment-wise planning for domestic vs. international earnings",
                "Representation before authorities when required",
            ],
        },
        {
            icon: <Globe />,
            title: "International Sports Taxation (DTAA)",
            description:
                "Comprehensive advisory for overseas tournament income, foreign league contracts, and global endorsements with full DTAA optimization to avoid double taxation while staying compliant in India.",
            points: [
                "Residency and source-based tax analysis",
                "Withholding tax reliefs and credits",
                "Foreign income disclosures and reporting",
            ],
        },
        {
            icon: <Shield />,
            title: "GST & Regulatory Compliance for Sports",
            description:
                "From athlete GST registration to filing and classification of sports services, we keep your compliance watertight across endorsements, brand collaborations, and event appearances.",
            points: [
                "Accurate HSN/SAC classification for sports services",
                "Monthly/quarterly returns without last-minute rush",
                "Vendor reconciliation and audit-ready records",
            ],
        },
        {
            icon: <Award />,
            title: "Endorsement & Sponsorship Advisory",
            description:
                "Deal structuring that balances commercial upside with tax efficiency—clear separation of IP licensing, appearance fees, and performance-linked elements to lower risk and maximize net income.",
            points: [
                "Contract review for tax exposure and clauses",
                "Royalty vs. professional income optimization",
                "Brand usage and image rights considerations",
            ],
        },
        {
            icon: <Users />,
            title: "Athlete Wealth & Retirement Planning",
            description:
                "Sports careers are short; financial goals are long. We build diversified, low-friction investment strategies tailored to cash-flow spikes, injury risk, and post-retirement lifestyle goals.",
            points: [
                "Goal-based portfolios with safety nets",
                "Tax-efficient mutual funds, debt, and alternatives",
                "Insurance and estate planning integration",
            ],
        },
        {
            icon: <Star />,
            title: "Audit & Assurance for Sports Entities",
            description:
                "Independent assurance for clubs, academies, franchises, and sports startups—transparent finances, cleaner governance, and investor-ready reporting.",
            points: [
                "Internal/statutory audits with sports-specific lens",
                "Controls for ticketing, merchandising, and grants",
                "Board and federation reporting support",
            ],
        },
    ];

    // --- FAQs ---
    const faqs = [
        {
            q: "Why do athletes need a specialized Sports CA instead of a regular accountant?",
            a: "Athletes deal with irregular cash flows, multiple income heads (endorsements, match fees, bonuses, prize money), and frequent cross-border tax issues. A specialized Sports CA anticipates these complexities, aligns deals with tax law, and protects you from penalties—while keeping your long-term wealth strategy intact.",
        },
        {
            q: "Can you handle international income and DTAA so I avoid double taxation?",
            a: "Yes. We analyze your tax residency, relevant treaties, and withholding to compute foreign tax credit (FTC) properly. We prepare compliant disclosures, so your global earnings are optimized and transparent under Indian law.",
        },
        {
            q: "Do you also help with investments and retirement planning for athletes?",
            a: "Absolutely. We build goal-based portfolios suited to short playing windows and long retirement horizons, including insurance cover, risk protection, and contingency funds to handle injury or career transitions.",
        },
        {
            q: "Do you work with sports businesses, academies, and franchises too?",
            a: "Yes. We support academies, clubs, leagues, and sports-tech/startups with accounting, GST, TDS, payroll, audits, valuations, and investor/cap table hygiene.",
        },
        {
            q: "How do I get started and what documents do you need?",
            a: "Book a  consultation first. We typically request prior ITRs, Form 26AS/AIS, GST credentials (if any), contract copies, bank statements, and a basic income map. From there, we craft a clean, actionable plan.",
        },
    ];

    return (
        <>
            {/* --- SEO TAGS --- */}
            <Helmet>
                <title>Best Sports CA Firm in India | Top Sports ca firm in india</title>
                <meta
                    name="description"
                    content="Best Sports CA Firm in India for athletes, teams, and academies. Tax planning, DTAA, GST, audits, and wealth management. Book a  consultation."
                />
                <meta
                    name="keywords"
                    content="Best sports CA firm in India, Top CA firm in India, Sports Chartered Accountant, Tax consultancy for sports, Sports accounting services"
                />
                <link rel="canonical" href="https://yourdomain.com/best-sports-ca-firm-india" />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content="Best Sports CA Firm in India | Athlete Tax Experts" />
                <meta
                    property="og:description"
                    content="India's Best Sports CA Firm: proactive tax planning, DTAA, GST, audit, and wealth planning for athletes and sports businesses."
                />
                <meta property="og:url" content="https://yourdomain.com/best-sports-ca-firm-india" />
                <meta property="og:image" content="https://yourdomain.com/assets/sports-ca-banner.jpg" />
                <meta property="og:site_name" content="Your CA Firm Name" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Best Sports CA Firm in India | Athlete Tax Experts" />
                <meta
                    name="twitter:description"
                    content="Tax planning, DTAA optimization, GST & audits for athletes and sports entities. Talk to India's best Sports CA team today."
                />
                <meta name="twitter:image" content="https://yourdomain.com/assets/sports-ca-banner.jpg" />

                {/* JSON-LD Schema */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "ProfessionalService",
                        name: "Best Sports CA Firm in India",
                        url: "https://yourdomain.com/best-sports-ca-firm-india",
                        description:
                            "A specialized Sports CA firm in India offering athlete taxation, DTAA, GST, audits, and wealth planning.",
                        areaServed: "IN",
                        sameAs: [
                            "https://facebook.com/yourpage",
                            "https://twitter.com/yourpage",
                            "https://www.linkedin.com/company/yourpage",
                        ],
                        logo: "https://yourdomain.com/assets/logo.png",
                        serviceType: [
                            "Athlete Tax Planning",
                            "International Taxation (DTAA)",
                            "GST Compliance",
                            "Audit & Assurance",
                            "Wealth & Retirement Planning",
                        ],
                        address: { "@type": "PostalAddress", addressCountry: "IN" },
                        contactPoint: {
                            "@type": "ContactPoint",
                            contactType: "Customer Service",
                            telephone: "+91-XXXXXXXXXX",
                            email: "info@yourdomain.com",
                        },
                    })}
                </script>
            </Helmet>

            {/* --- PAGE --- */}
            <div className="min-h-screen">
                {/* HERO (Primary) */}
                <section className="bg-[#00597b] text-white py-20 text-center px-6">
                    <h1 className="text-4xl md:text-6xl font-bold mb-6">
                        Best Sports CA Firm in India
                    </h1>
                    <p className="text-xl max-w-4xl mx-auto mb-8 leading-relaxed">
                        As the <strong>Best Sports CA Firm in India</strong>, we help athletes, teams, academies,
                        and sports businesses simplify taxes, stay compliant, and build long-term wealth. From  league contracts , international appearances to sponsorships and endorsements—our specialized
                        playbook delivers clarity, control, and confidence while you focus on winning.
                    </p>
                    <Link to={URLs.QUERIES}>
                        <button className="bg-white text-[#00597b] px-8 py-4 rounded-xl text-lg font-semibold hover:bg-[#ccf1ff] transition inline-flex items-center">
                            Get  Consultation <ChevronRight className="w-5 h-5 ml-2" />
                        </button>
                    </Link>
                </section>

                {/* INTRO (Secondary) */}
                <section className="bg-[#ccf1ff] text-[#00597b] py-16 px-6">
                    <div className="max-w-5xl mx-auto">
                        <h2 className="text-3xl font-bold mb-6">Why Financial Precision Matters in Sports</h2>
                        <p className="text-lg leading-relaxed mb-4">
                            India’s sports economy has exploded—leagues across cricket, football, kabaddi, and more
                            now create multiple income channels for athletes: match fees, prize money, endorsements,
                            IP licensing, social collaborations, and overseas tournaments. Each stream is taxed
                            differently, and the timing of payouts rarely matches a neat monthly salary. Add cross-border
                            tournaments, changing residencies, and complex contracts—and you need a specialized team
                            that understands sports finance inside out.
                        </p>
                        <p className="text-lg leading-relaxed mb-4">
                            That’s exactly what the <strong>Best Sports CA Firm in India</strong> should do—convert
                            complexity into a predictable, compliant system. We forecast liabilities, time deductions
                            smartly, structure endorsement contracts, document DTAA reliefs, and keep books audit-ready.
                            The result: more take-home, fewer surprises, and a future-proof plan for life after sport.
                        </p>
                        <p className="text-lg leading-relaxed">
                            Whether you’re an established pro, an academy founder, a franchise CFO, or an athlete
                            entering your first major league, our frameworks scale with your career—keeping your
                            finances nimble, compliant, and aligned with your goals.
                        </p>
                    </div>
                </section>

                {/* BENEFITS (Primary) */}
                <section className="bg-[#00597b] text-white py-16 px-6">
                    <div className="max-w-6xl mx-auto">
                        <h2 className="text-3xl font-bold text-center mb-10">
                            What Sets the Best Sports CA Firm in India Apart?
                        </h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-base">
                            {[
                                "Proactive tax planning for every income stream—no last-minute chaos.",
                                "DTAA-aligned strategies to prevent double taxation on foreign earnings.",
                                "Crystal-clear GST mapping for endorsements, appearances, and brand deals.",
                                "Goal-based wealth planning aligned to short careers and long retirements.",
                                "Audit-ready records, reconciliations, and clean documentation.",
                                "Confidential, athlete-first advice with risk management at its core.",
                            ].map((b, i) => (
                                <div key={i} className="bg-white/10 rounded-xl p-5 flex items-start gap-3">
                                    <CheckCircle className="w-6 h-6 flex-shrink-0" />
                                    <p className="leading-relaxed">{b}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* SERVICES (Secondary) */}
                <section className="bg-[#ccf1ff] text-[#00597b] py-16 px-6">
                    <div className="max-w-6xl mx-auto">
                        <h2 className="text-3xl font-bold text-center mb-10">Comprehensive Services</h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {services.map((s, idx) => (
                                <div key={idx} className="bg-white rounded-2xl shadow p-6">
                                    <div className="mb-4">
                                        {React.cloneElement(s.icon, { className: "w-10 h-10 text-[#00597b]" })}
                                    </div>
                                    <h3 className="text-xl font-semibold mb-2">{s.title}</h3>
                                    <p className="mb-3">{s.description}</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        {s.points.map((p, i) => (
                                            <li key={i}>{p}</li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* PROCESS (Primary) */}
                <section className="bg-[#00597b] text-white py-16 px-6">
                    <div className="max-w-5xl mx-auto">
                        <h2 className="text-3xl font-bold text-center mb-8">Our Playbook: Simple, Predictable, Effective</h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            {[
                                {
                                    icon: <Briefcase />,
                                    title: "Discovery & Mapping",
                                    text: "We unpack your revenue sources, contracts, residencies, and obligations. You’ll get a clear snapshot of current liabilities, gaps, and opportunities.",
                                },
                                {
                                    icon: <BarChart4 />,
                                    title: "Custom Strategy",
                                    text: "We design a strategy for tax timing, DTAA reliefs, GST classification, and cash-flow smoothing—tailored to your season calendar and endorsement pipeline.",
                                },
                                {
                                    icon: <FileCheck2 />,
                                    title: "Execution & Compliance",
                                    text: "We file on time, reconcile vendor data, prepare disclosures, and keep documents audit-ready so you never panic during assessments or inquiries.",
                                },
                                {
                                    icon: <Handshake />,
                                    title: "Review & Wealth Building",
                                    text: "Quarterly reviews align your finances to real-world changes—injuries, transfers, league changes—while your long-term investments keep compounding.",
                                },
                            ].map((step, i) => (
                                <div key={i} className="bg-white/10 rounded-xl p-6">
                                    <div className="mb-3">
                                        {React.cloneElement(step.icon, { className: "w-8 h-8" })}
                                    </div>
                                    <h3 className="text-xl font-semibold mb-1">{step.title}</h3>
                                    <p className="leading-relaxed">{step.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CASE STUDIES (Secondary) */}
                <section className="bg-[#ccf1ff] text-[#00597b] py-16 px-6">
                    <div className="max-w-6xl mx-auto">
                        <h2 className="text-3xl font-bold text-center mb-8">Real Impact: Mini Case Studies</h2>
                        <div className="grid md:grid-cols-3 gap-8">
                            {/* Case Study 1 */}
                            <div className="bg-white rounded-2xl shadow p-6">
                                <h3 className="text-xl font-semibold mb-2">International Cricketer</h3>
                                <p className="leading-relaxed">
                                    Multiple brand deals, domestic league fees, and overseas match earnings had created
                                    a tangled tax trail. We re-structured endorsement contracts, implemented DTAA-compliant
                                    disclosures, and streamlined GST on appearances. Outcome: fewer leakages, faster filings,
                                    and a sizable improvement in post-tax income—without increasing risk.
                                </p>
                            </div>

                            {/* Case Study 2 */}
                            <div className="bg-white rounded-2xl shadow p-6">
                                <h3 className="text-xl font-semibold mb-2">Football Academy & Merch</h3>
                                <p className="leading-relaxed">
                                    A growing academy struggled with fee invoicing, merchandising GST, and grant audits.
                                    Our audit and compliance stack standardized fee accounting, optimized input credits,
                                    and built a clean documentation trail for sponsors and federations—unlocking smoother
                                    cash flows and better governance.
                                </p>
                            </div>

                            {/* Case Study 3 (Kabaddi League) */}
                            <div className="bg-white rounded-2xl shadow p-6">
                                <h3 className="text-xl font-semibold mb-2">Kabaddi League</h3>
                                <p className="leading-relaxed">
                                    The league faced complexities in central revenue allocation, sponsorship inflows,
                                    and multi-state GST compliance for matches. We designed a unified accounting framework,
                                    automated statutory filings, and implemented clear revenue-sharing protocols—ensuring
                                    financial transparency and timely compliance across the season.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>


                {/* WHY CHOOSE US (Primary) */}
                <section className="bg-[#00597b] text-white py-16 px-6">
                    <div className="max-w-5xl mx-auto">
                        <h2 className="text-3xl font-bold text-center mb-8">Why Athletes & Sports Businesses Choose Us</h2>
                        <ul className="space-y-4 text-lg">
                            <li className="bg-white/10 rounded-xl p-5">
                                <strong>Sports-native expertise:</strong> We speak the language of leagues, contracts,
                                image rights, and federation rules—so advice is practical, not generic.
                            </li>
                            <li className="bg-white/10 rounded-xl p-5">
                                <strong>Compliance without friction:</strong> Digital workflows, periodic reminders,
                                and reconciliations keep filings on track and stress levels low.
                            </li>
                            <li className="bg-white/10 rounded-xl p-5">
                                <strong>Confidential & athlete-first:</strong> We prioritize privacy, brand safety,
                                and reputational risk across every decision we recommend.
                            </li>
                            <li className="bg-white/10 rounded-xl p-5">
                                <strong>Future-ready planning:</strong> Injury cover, career transitions, and post-retirement
                                income are built into your plan from day one.
                            </li>
                        </ul>
                        <p className="mt-6 leading-relaxed">
                            If you’re searching for the <strong>Best Sports CA Firm in India</strong>, choose a partner
                            that protects your present and builds your future—with rigor, discretion, and real sports
                            context.
                        </p>
                    </div>
                </section>

                {/* FAQ (Primary to keep strong contrast and continuity before CTA) */}
                <section className="bg-[#ccf1ff] text-gray-900 py-16 px-6">
                    <div className="max-w-4xl mx-auto">
                        <h2 className="text-3xl font-bold text-center mb-6">
                            Frequently Asked Questions
                        </h2>
                        <div className="space-y-4">
                            {faqs.map((f, i) => (
                                <div
                                    key={i}
                                    className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200"
                                >
                                    <button
                                        onClick={() => setExpandedFAQ(expandedFAQ === i ? null : i)}
                                        className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50"
                                    >
                                        <h3 className="text-lg font-semibold">{f.q}</h3>
                                        <ChevronRight
                                            className={`w-5 h-5 text-gray-500 transition-transform ${expandedFAQ === i ? "rotate-90" : ""
                                                }`}
                                        />
                                    </button>
                                    {expandedFAQ === i && (
                                        <div className="px-6 pb-5 text-gray-700">{f.a}</div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>


                {/* CTA (Primary) */}
                <section className="bg-[#00597b] text-white py-16 text-center px-6">
                    <h2 className="text-3xl font-bold mb-4">
                        Work with the Best Sports CA Firm in India
                    </h2>
                    <p className="text-lg max-w-3xl mx-auto mb-6 leading-relaxed">
                        Book a consultation and get a personalized action plan for taxes, DTAA, GST,
                        audits, and wealth—tailored to your sport, your calendar, and your goals.
                    </p>
                    <Link to={URLs.QUERIES}>
                        <button className="bg-white text-[#00597b] px-8 py-4 rounded-xl text-lg font-semibold hover:bg-[#ccf1ff] transition inline-flex items-center">
                            Book  Consultation <ChevronRight className="w-5 h-5 ml-2" />
                        </button>
                    </Link>
                    <p className="mt-4 text-white/80">
                        Email: sjasonipat@gmail.com &nbsp;•&nbsp; Phone: +91-8684893331
                    </p>
                </section>
            </div>
        </>
    );
};

export default SportsCAWebsite;
